/**
 * Create a Business Discovery and hand over the client link.
 *
 * The same call the console has always made: one POST that issues a link for
 * one client. Nothing else is created or sent from here.
 */

import { useState } from 'react';
import { createAssessment } from '../api/client';
import { friendlyError } from './consoleData';

function clientUrl(path) {
  return `${window.location.origin}${path}`;
}

export default function NewDiscoveryLink({ onCreated }) {
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const link = await createAssessment();
      setIssued(link);
      await onCreated?.();
    } catch (cause) {
      setError(friendlyError(cause));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(clientUrl(issued.assessment_path));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="nsc-card nsc-issue" aria-labelledby="nsc-issue-title">
      <div className="nsc-issue__row">
        <div className="nsc-issue__text">
          <h2 id="nsc-issue-title" className="nsc-card__title">Start a new Business Discovery</h2>
          <p className="nsc-card__description">
            Create a private link for one client. They open it and speak with NS
            Assistant; the conversation is saved and reported on automatically
            when it finishes.
          </p>
        </div>
        <button
          type="button"
          className="nsc-button nsc-button--primary"
          onClick={create}
          disabled={busy}
        >
          {busy ? 'Creating link…' : 'Create client link'}
        </button>
      </div>

      {error && <p className="nsc-alert nsc-alert--danger" role="alert">{error}</p>}

      {issued && (
        <div className="nsc-issue__link" role="status">
          <span className="nsc-issue__label">Client link</span>
          <code className="nsc-issue__url">{clientUrl(issued.assessment_path)}</code>
          <div className="nsc-issue__actions">
            <button type="button" className="nsc-button nsc-button--secondary" onClick={copy}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <a
              className="nsc-button nsc-button--ghost"
              href={issued.assessment_path}
              target="_blank"
              rel="noreferrer"
            >
              Open
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
